import { useEffect, useRef, useState } from "react";

import { IconLogout, IconPhone, IconSettings } from "./icons.js";

/**
 * Accountknop rechtsboven, zoals Outlook: een cirkel met de eerste letter van
 * het adres, met daaronder een uitklapmenu. Dit verving drie losse knoppen plus
 * het uitgeschreven adres in de bovenbalk — samen ~370px die op een telefoon
 * het logo van het scherm drukten.
 */

interface Props {
  address: string;
  onDeviceSetup: () => void;
  onSettings: () => void;
  onLogout: () => void;
}

export function AccountMenu({ address, onDeviceSetup, onSettings, onLogout }: Props): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const letter = address.charAt(0).toUpperCase() || "?";

  // Sluiten bij klik/tik buiten knop én menu. De listener bestaat alleen
  // zolang het menu open is; permanent luisteren op document is verspilling.
  useEffect(() => {
    if (!open) return;

    function onPointerDown(e: PointerEvent): void {
      const target = e.target as Node;
      if (buttonRef.current?.contains(target) === true) return;
      if (menuRef.current?.contains(target) === true) return;
      setOpen(false);
    }

    function onKey(e: KeyboardEvent): void {
      if (e.key === "Escape") {
        setOpen(false);
        // Focus terug naar de knop, anders belandt de toetsenbordgebruiker
        // in het niets en moet hij opnieuw naar de balk tabben.
        buttonRef.current?.focus();
        return;
      }
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        const items = menuRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]');
        if (!items || items.length === 0) return;
        const list = Array.from(items);
        const index = list.indexOf(document.activeElement as HTMLButtonElement);
        const next =
          e.key === "ArrowDown"
            ? list[(index + 1) % list.length]
            : list[(index - 1 + list.length) % list.length];
        next?.focus();
      }
      // Tab laten we met rust: de browser verlaat het menu dan vanzelf, en de
      // pointerdown-listener ruimt de open-status op bij de volgende klik.
      if (e.key === "Tab") setOpen(false);
    }

    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Bij openen meteen het eerste item focussen, zodat pijltjes en Enter
  // direct werken zonder eerst te hoeven tabben.
  useEffect(() => {
    if (!open) return;
    const first = menuRef.current?.querySelector<HTMLButtonElement>('[role="menuitem"]');
    first?.focus();
  }, [open]);

  function kies(actie: () => void): void {
    setOpen(false);
    actie();
  }

  return (
    <div className="account">
      <button
        ref={buttonRef}
        type="button"
        className="avatar-button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Account: ${address}`}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="avatar" aria-hidden="true">{letter}</span>
      </button>

      {open && (
        <div ref={menuRef} className="account-menu" role="menu" aria-label="Account">
          <div className="account-menu-head">
            <span className="avatar" aria-hidden="true">{letter}</span>
            <span className="account-menu-address">{address}</span>
          </div>

          <button type="button" role="menuitem" className="account-menu-item" onClick={() => kies(onDeviceSetup)}>
            <IconPhone size={18} />
            Op mijn telefoon
          </button>
          <button type="button" role="menuitem" className="account-menu-item" onClick={() => kies(onSettings)}>
            <IconSettings size={18} />
            Instellingen
          </button>

          <div className="account-menu-sep" role="separator" />

          <button type="button" role="menuitem" className="account-menu-item" onClick={() => kies(onLogout)}>
            <IconLogout size={18} />
            Uitloggen
          </button>
        </div>
      )}
    </div>
  );
}
